import { useState } from "react";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import { useClickOutside } from "../../hooks/useClickOutside";
import { fireWrite } from "../../utils/offlineWrites";
import { findTravelPartyKey } from "../../utils/locationSharing";
import type { Trip } from "../../types";

interface LocationSharingControlProps {
  tripId: string;
  trip: Pick<Trip, "travelParty">;
  /** The user's account-level default sharing preference. */
  accountDefault: boolean;
}

type Choice = "default" | "on" | "off";

/**
 * Per-trip location-sharing override for the signed-in member of a trip,
 * surfaced as a compact on/off location icon in the trip's title card. The
 * popover explains the current state and lets you switch. Writes
 * `travelParty[me].locationSharingEnabled` as `null` (inherit account default),
 * `true`, or `false`. Only rendered when the current user has a travel-party
 * entry on this trip.
 */
export function LocationSharingControl({ tripId, trip, accountDefault }: LocationSharingControlProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useClickOutside<HTMLDivElement>(open, () => setOpen(false));

  const memberKey = user ? findTravelPartyKey(trip, user.uid) : null;
  if (!user || !memberKey) return null;

  const current = trip.travelParty?.[memberKey]?.locationSharingEnabled ?? null;
  const choice: Choice = current === true ? "on" : current === false ? "off" : "default";
  const effective = current === null ? accountDefault : current;

  const update = (next: Choice) => {
    if (next === choice) return;
    const value = next === "on" ? true : next === "off" ? false : null;
    fireWrite(
      updateDoc(doc(db, "trips", tripId), {
        [`travelParty.${memberKey}.locationSharingEnabled`]: value,
        updatedAt: serverTimestamp(),
      }),
      "trip location sharing",
    );
  };

  const options: { value: Choice; label: string }[] = [
    { value: "default", label: `Use default (${accountDefault ? "on" : "off"})` },
    { value: "on", label: "Share" },
    { value: "off", label: "Don't share" },
  ];

  return (
    <div className="trip-pass-menu" ref={ref}>
      <button
        type="button"
        className={`dp-menu-trigger trip-loc-btn ${effective ? "on" : "off"}`}
        onClick={() => setOpen((o) => !o)}
        aria-label="Location sharing"
        aria-pressed={effective}
        title={effective ? "Sharing your location" : "Not sharing your location"}
      >
        📍
      </button>
      {open && (
        <div className="detail-menu-dropdown trip-loc-pop">
          <div className="trip-loc-pop-hint">
            {effective
              ? "Your trail is visible to this trip's travel party on the Journey map."
              : "Your location is not shared with this trip's travel party."}
          </div>
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`trip-loc-pop-opt${choice === opt.value ? " on" : ""}`}
              onClick={() => update(opt.value)}
              aria-pressed={choice === opt.value}
            >
              {choice === opt.value ? "✓ " : ""}
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
